import { TutorialDefinition } from '../TutorialEngine.js';
import { IgnitionRenderOptions } from './IgnitionTutorialAdapter.js';
import { COLORS } from '../../rendering/Renderer.js';

type IgnitionScene = {
    gameState: string;
    renderOptions: IgnitionRenderOptions;
    annotations: TutorialDefinition['scenes'][number]['annotations'];
    animate?: {
        interval: number;
        loop: boolean;
        frames: string[];
    };
};

// Grid: 6/1-3:1E2,2A3,3D4
// Center 2x2: cells C2, D2, C3, D3
// Rings: ring 0 at offset 1 (rows/cols 1-5), ring 1 at offset 2 (rows/cols 2-4)
// Full solution: 4344,5354,3242,2434,2535,4555

const scenes: IgnitionScene[] = [
    // Scene 1: Goal — what a solved puzzle looks like
    {
        gameState: '4344,5354,3242,2434,2535,4555',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'In Ignition, place fuses to\nignite all digits in order', cell: 'D0', anchor: 'center', color: COLORS.teal },
        ],
    },

    // Scene 2: Center fire
    {
        gameState: '',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Fire starts here\nat time 0', cell: 'C1', anchor: 'center' },
            { type: 'text-label', text: 'Concentric walls\ndivide the grid\ninto chambers', cell: 'B4', anchor: 'near-below' },
            { type: 'pulse', cell: 'C2', color: COLORS.amber },
            { type: 'pulse', cell: 'D2', color: COLORS.amber },
            { type: 'pulse', cell: 'C3', color: COLORS.amber },
            { type: 'pulse', cell: 'D3', color: COLORS.amber },
        ],
    },

    // Scene 3: Fuse placement and propagation
    {
        gameState: '3242,3141',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Click an edge\nto place a fuse', cell: 'B0', anchor: 'center' },
            { type: 'text-label', text: 'Fire travels one\ntime unit per segment', cell: 'C5', anchor: 'center' },
            { type: 'pulse', edge: '3141', color: COLORS.teal },
            { type: 'pulse', edge: '3040', color: COLORS.teal },
        ],
        animate: {
            interval: 1500,
            loop: true,
            frames: [
                '3242',
                '3242,3141',
                '3242,3141,3040',
            ],
        },
    },

    // Scene 4: Fuses are walls and divide chambers
    {
        gameState: '3242,3141,2434,2535,1121',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Fuses also act\nas walls', cell: 'B1', anchor: 'center' },
            { type: 'text-label', text: 'Chambers can\nbe divided', cell: 'E5', anchor: 'center' },
        ],
    },

    // Scene 5: No branching
    {
        gameState: '3242,4344,5354,3141,3132',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Fuses can\'t\nbranch', cell: 'D0', anchor: 'right', color: COLORS.red },
            { type: 'text-label', text: 'Fuses can\nbend', cell: 'B0', anchor: 'center' },
            { type: 'pulse', cell: 'D1', color: COLORS.red },
        ],
        animate: {
            interval: 1500,
            loop: true,
            frames: [
                '3242,4344,5354,3141,3132',
                '3242,4344,5354,3132',
                '3242,4344,5354',
            ],
        },
    },

    // Scene 6: Ignite in order, badge warning
    {
        gameState: '4344,5354,2131,2232,2324,3242',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Each digit must\nignite later\nthan the previous', cell: 'E0', anchor: 'center' },
            { type: 'text-label', text: 'Amber badge =\nwrong order', cell: 'D5', anchor: 'center', color: COLORS.amber },
        ],
        animate: {
            interval: 2000,
            loop: true,
            frames: [
                '4344,5354,2131,2232,2324,3242',
                '4344,5354,2131,2232,2324,3242,1424',
                '4344,5354,2131,2232,2324,3242,1424,2425',
            ],
        },
    },

];

/**
 * Ignition tutorial: 6 scenes on a 6x6 grid with digits 1-3.
 * See docs/tutorials/ignition.md for detailed scene descriptions.
 */
export const IGNITION_TUTORIAL: TutorialDefinition = {
    gameType: 'ignition',
    gridCode: '6/1-3:1E2,2A3,3D4',
    scenes,
};
